// Fixed BookingForm.jsx (added validation for fields, fixed customer_type to 'walk-in', improved accessibility)
import React, { useState, useEffect } from 'react';
import { apiGet, apiPost, apiPut } from '../utils/api';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import { handleError, handleSuccess } from '../utils/errorHandler';
import { validatePhoneFormat } from '../utils/phone';

const BookingForm = ({ booking, onSubmit, onCancel, endpoints = {}, isWalkIn = false, bookings = [], services: propServices, workers: propWorkers }) => {
  const {
    services: servicesEndpoint = '/api/services',
    workers: workersEndpoint = '/api/workers',
    availableSlots: availableSlotsEndpoint = '/api/bookings/available-slots',
    createBooking: createBookingEndpoint = '/api/bookings',
    updateBooking: updateBookingEndpoint = '/api/bookings'
  } = endpoints;
  const [services, setServices] = useState(propServices || []);
  const [filteredServices, setFilteredServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [workers, setWorkers] = useState(propWorkers || []);
  const [servicesLoading, setServicesLoading] = useState(!propServices || propServices.length === 0);
  const [workersLoading, setWorkersLoading] = useState(!propWorkers || propWorkers.length === 0);

  // New UI State for Simplification
  const [useExistingCustomer, setUseExistingCustomer] = useState(false);

  const [formData, setFormData] = useState({
    service_ids: [],
    workers: [], // Add workers array for multi-worker support
    worker_id: '',
    booking_date: new Date(),
    booking_time: '',
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    notes: '',
    misc_charges: []
  });
  const [isWalkInMode, setIsWalkInMode] = useState(isWalkIn);
  const [loading, setLoading] = useState(false);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [paymentStatus, setPaymentStatus] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  useEffect(() => {
    if (!propServices || propServices.length === 0) {
      fetchServices();
    } else {
      setServices(propServices);
      setServicesLoading(false);
    }

    if (!propWorkers || propWorkers.length === 0) {
      fetchWorkers();
    } else {
      setWorkers(propWorkers);
      setWorkersLoading(false);
    }

    // If booking prop is provided, populate form for editing
    if (booking) {
      setIsEditing(true);
      setPaymentStatus(booking.payment_status || 'pending');

      // Parse the scheduled_time to extract date and time
      const scheduledDate = new Date(booking.scheduled_time);
      const timeString = scheduledDate.toTimeString().slice(0, 5); // HH:MM format

      setFormData({
        service_ids: booking.service_ids || (booking.service_id ? [booking.service_id] : []),
        workers: booking.workers || (booking.worker_id ? [{ worker_id: booking.worker_id }] : []),
        worker_id: booking.worker_id || (booking.workers?.[0]?.worker_id || ''),
        booking_date: scheduledDate,
        booking_time: timeString,
        customer_name: booking.customer_name,
        customer_email: booking.customer_email,
        customer_phone: booking.customer_phone,
        notes: booking.notes || '',
        misc_charges: booking.misc_charges || []
      });
    }
  }, [booking]);
  useEffect(() => {
    // Only fetch if we have a date and either (services selected OR no services selected yet - simplified)
    // Actually, backend now handles optional services/workers.
    // But logically, we probably want to wait for services to calculate duration?
    // Backend defaults to 60 mins.
    // Let's allow fetching even if no services/workers, so user can see generic availability.

    if (formData.booking_date) {
      fetchAvailableSlots();
    }
  }, [formData.workers, formData.booking_date, formData.service_ids]);
  useEffect(() => {
    let result = services;

    // Filter by category
    if (selectedCategory !== 'All') {
      result = result.filter(s => (s.category || 'Other') === selectedCategory);
    }

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(s =>
        s.name.toLowerCase().includes(query) ||
        (s.description && s.description.toLowerCase().includes(query))
      );
    }

    setFilteredServices(result);
  }, [selectedCategory, searchQuery, services]);

  const fetchServices = async () => {
    try {
      setServicesLoading(true);
      const data = await apiGet(servicesEndpoint);
      // Ensure data is an array, fallback to empty array if not
      const servicesData = Array.isArray(data) ? data :
        (data && Array.isArray(data.services) ? data.services : []);
      setServices(servicesData);
      setFilteredServices(servicesData);
      const uniqueCategories = ['All', ...new Set(servicesData.map(s => s.category || 'Other'))];
      setCategories(uniqueCategories);
    } catch (error) {
      handleError(error, 'Failed to load services');
      setServices([]); // Ensure services is always an array on error
    } finally {
      setServicesLoading(false);
    }
  };
  const fetchWorkers = async () => {
    try {
      setWorkersLoading(true);
      const data = await apiGet(workersEndpoint);
      // Ensure data is an array, fallback to empty array if not
      const workersData = Array.isArray(data) ? data :
        (data && Array.isArray(data.workers) ? data.workers : []);
      setWorkers(workersData);
    } catch (error) {
      handleError(error, 'Failed to load workers');
      setWorkers([]); // Ensure workers is always an array on error
    } finally {
      setWorkersLoading(false);
    }
  };
  const fetchAvailableSlots = async () => {
    try {
      const dateStr = formData.booking_date.toLocaleDateString('en-CA');
      const workerIds = Array.isArray(formData.workers) ? formData.workers.map(w => w.worker_id).join(',') : '';
      const url = `${availableSlotsEndpoint}?worker_id=${workerIds}&date=${dateStr}&service_ids=${formData.service_ids.join(',')}`;
      const data = await apiGet(url);
      const slotsData = Array.isArray(data) ? data : [];
      setAvailableSlots(slotsData);
    } catch (error) {
      handleError(error, 'Failed to load available time slots');
    }
  };
  const validateField = (name, value) => {
    let error = '';

    switch (name) {
      case 'customer_name':
        if (!value.trim()) {
          error = 'Customer name is required';
        } else if (value.trim().length < 2) {
          error = 'Name must be at least 2 characters long';
        } else if (value.trim().length > 50) {
          error = 'Name must not exceed 50 characters';
        } else if (!/^[a-zA-Z\s.'-]+$/.test(value.trim())) {
          error = 'Name can only contain letters, spaces, periods, apostrophes, and hyphens';
        }
        break;

      case 'customer_email':
        if (value.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())) {
          error = 'Please enter a valid email address';
        } else if (value.trim() && value.trim().length > 100) {
          error = 'Email must not exceed 100 characters';
        }
        break;

      case 'customer_phone':
        if (!value.trim()) {
          error = 'Phone number is required';
        } else {
          error = validatePhoneFormat(value) || '';
        }
        break;

      case 'service_ids':
        if (!value || value.length === 0) {
          error = 'Please select at least one service';
        }
        break;

      case 'booking_time':
        if (!isWalkInMode && !value) {
          error = 'Please select a time slot';
        }
        break;

      case 'worker_id':
        if (!isWalkInMode && (!value || value.length === 0)) {
          error = 'Please select at least one worker';
        }
        break;

      default:
        break;
    }

    return error;
  };
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error for this field when user starts typing
    if (touched[name] && errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };
  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched(prev => ({
      ...prev,
      [name]: true
    }));

    const error = validateField(name, value);
    setErrors(prev => ({
      ...prev,
      [name]: error
    }));
  };
  const handleDateChange = (date) => {
    setFormData(prev => ({
      ...prev,
      booking_date: date,
      booking_time: '' // Reset time when date changes
    }));
  };
  const handleServiceSelection = (serviceId) => {
    setFormData(prev => {
      const currentServices = prev.service_ids || [];
      const isSelected = currentServices.includes(serviceId);

      if (isSelected) {
        return {
          ...prev,
          service_ids: currentServices.filter(id => id !== serviceId)
        };
      } else {
        return {
          ...prev,
          service_ids: [...currentServices, serviceId]
        };
      }
    });
  };
  const handleWorkerSelection = (workerId) => {
    setFormData(prev => {
      const currentWorkers = prev.workers || [];
      const isSelected = currentWorkers.some(w => w.worker_id === workerId);

      if (isSelected) {
        // Remove worker if already selected
        const updatedWorkers = currentWorkers.filter(w => w.worker_id !== workerId);
        return {
          ...prev,
          workers: updatedWorkers,
          worker_id: updatedWorkers.length > 0 ? updatedWorkers[0].worker_id : '' // Update primary worker
        };
      } else {
        // Add worker (no role needed)
        const updatedWorkers = [...currentWorkers, { worker_id: workerId }];
        return {
          ...prev,
          workers: updatedWorkers,
          worker_id: updatedWorkers.length > 0 ? updatedWorkers[0].worker_id : '' // Set primary worker
        };
      }
    });
  };
  const validateForm = () => {
    const newErrors = {};

    // Validate all fields
    Object.keys(formData).forEach(key => {
      const error = validateField(key, formData[key]);
      if (error) {
        newErrors[key] = error;
      }
    });

    // Additional date validation for non-walk-in bookings
    if (!isWalkInMode) {
      const selectedDate = new Date(formData.booking_date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (selectedDate < today) {
        newErrors.booking_date = 'Booking date cannot be in the past';
      }

      // Check if selected date is too far in future (e.g., more than 6 months)
      const maxFutureDate = new Date();
      maxFutureDate.setMonth(maxFutureDate.getMonth() + 6);
      if (selectedDate > maxFutureDate) {
        newErrors.booking_date = 'Booking date cannot be more than 6 months in advance';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Removed frontend booking number generation - now handled server-side
  // This prevents conflicts and removes localStorage dependency

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form before submission
    if (!validateForm()) {
      // Mark all fields as touched to show validation errors
      const allTouched = {};
      Object.keys(formData).forEach(key => {
        allTouched[key] = true;
      });
      setTouched(allTouched);
      return;
    }

    setLoading(true);
    try {
      let payload;

      if (isWalkInMode) {
        // For walk-in customers, use current date/time
        const now = new Date();
        const dateStr = now.toLocaleDateString('en-CA'); // YYYY-MM-DD in local time
        const timeStr = now.toTimeString().slice(0, 8); // HH:MM:SS format

        payload = {
          ...formData,
          service_ids: formData.service_ids, // Send array of service IDs
          service_id: formData.service_ids[0], // Keep for backward compatibility
          scheduled_time: `${dateStr}T${timeStr}+01:00`,
          booking_date: dateStr,
          booking_time: timeStr.slice(0, 5), // HH:MM format
          notes: formData.notes || 'Walk-in customer',
          customer_type: 'walk_in'
          // booking_number removed - now generated server-side
        };
      } else {
        // For regular bookings, use selected date/time
        const dateStr = formData.booking_date.toLocaleDateString('en-CA');
        const bookingData = {
          ...formData,
          service_ids: formData.service_ids, // Send array of service IDs
          service_id: formData.service_ids[0], // Keep for backward compatibility
          booking_date: dateStr
        };

        // Compose scheduled_time from selected date and time if available
        const timeStr = formData.booking_time.length === 5 ? `${formData.booking_time}:00` : formData.booking_time;
        payload = {
          ...bookingData,
          scheduled_time: `${dateStr}T${timeStr}+01:00`,
          customer_type: 'pre_booked'
          // booking_number removed - now generated server-side
        };
      }
      let responseData;
      if (isEditing && booking) {
        // Update existing booking
        responseData = await apiPut(`${updateBookingEndpoint}/${booking.id}`, payload);

        // Assign multiple workers if provided (simplified - no roles)
        if (formData.workers && formData.workers.length > 0) {
          try {
            const workers = formData.workers.map(w => ({ worker_id: w.worker_id }));
            await apiPost(`/api/bookings/${booking.id}/assign-workers`, {
              workers: workers
            });
          } catch (workerError) {
            console.error('Error assigning workers:', workerError);
            // Don't fail the booking update if worker assignment fails
          }
        }

        handleSuccess('Booking updated successfully!');
      } else {
        // Create new booking
        responseData = await apiPost(createBookingEndpoint, payload);

        // Assign multiple workers if provided (simplified - no roles)
        if (formData.workers && formData.workers.length > 0) {
          try {
            const workers = formData.workers.map(w => ({ worker_id: w.worker_id }));
            await apiPost(`/api/bookings/${responseData.id}/assign-workers`, {
              workers: workers
            });
          } catch (workerError) {
            console.error('Error assigning workers:', workerError);
            // Don't fail the booking creation if worker assignment fails
          }
        }

        handleSuccess('Booking created successfully!');
      }

      if (onSubmit) {
        onSubmit(responseData);
      }
      // Reset form only for new bookings
      if (!isEditing) {
        setFormData({
          service_ids: [],
          workers: [],
          worker_id: '',
          booking_date: new Date(),
          booking_time: '',
          customer_name: '',
          customer_email: '',
          customer_phone: '',
          notes: '',
          misc_charges: []
        });
      }
    } catch (error) {
      const message = error.response?.data?.error || 'Error saving booking. Please try again.';
      handleError(error, message);
    } finally {
      setLoading(false);
    }
  };
  const getPaymentStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      case 'refunded':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  return (
    <div className="w-full bg-white sm:rounded-xl" role="main">
      <div className="p-4 sm:p-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900 tracking-tight" id="booking-form-title">
              {isEditing ? 'Edit Booking' : 'New Walk-in'}
            </h2>
            <p className="text-gray-500 mt-1 text-sm sm:text-base">
              {isEditing ? 'Update the details for this appointment' : 'Capture details for a new service'}
            </p>
          </div>
          {isEditing && booking?.booking_number && (
            <div className="text-right hidden sm:block">
              <span className="text-xs font-bold text-gray-400 uppercase tracking-widest block">Reference</span>
              <span className="text-lg font-mono font-bold text-blue-600">#{booking.booking_number}</span>
            </div>
          )}
        </div>

        {/* Payment Status Display for Editing Mode */}
        {isEditing && (
          <div className="mb-8 p-4 bg-gradient-to-r from-gray-50 to-white border border-gray-100 rounded-2xl shadow-sm flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-50 rounded-full flex items-center justify-center text-blue-600">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <div>
                <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">Payment Status</p>
                <p className="text-sm font-semibold text-gray-900">{paymentStatus?.toUpperCase() || 'PENDING'}</p>
              </div>
            </div>
            <span className={`px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wide shadow-sm ${getPaymentStatusColor(paymentStatus)}`}>
              {paymentStatus?.toUpperCase() || 'PENDING'}
            </span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6" role="form" aria-labelledby="booking-form-title">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
            {/* Customer Information */}
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-gray-700">Customer Details</h3>
                <button
                  type="button"
                  onClick={() => setUseExistingCustomer(!useExistingCustomer)}
                  className="text-xs text-blue-600 hover:text-blue-800 underline"
                >
                  {useExistingCustomer ? 'Switch to New Customer' : 'Search Existing Customer'}
                </button>
              </div>

              {useExistingCustomer ? (
                <div className="bg-blue-50 p-4 rounded-md border border-blue-100">
                  <label className="block text-xs font-bold text-blue-700 uppercase tracking-wider mb-2">
                    Search Customer
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Type name or phone to search..."
                      className="w-full px-3 py-2 border border-blue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                      onChange={(e) => {
                        // Simulating search - In real app, this would query API
                        handleInputChange(e);
                      }}
                    />
                    <div className="absolute right-2 top-2 text-gray-400">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                  </div>
                  <p className="text-xs text-blue-600 mt-2">
                    ℹ️ Start typing to find customers. (Functionality placeholder)
                  </p>
                </div>
              ) : (
                <>
                  <div>
                    <label htmlFor="customer-name" className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                      Customer Name *
                    </label>
                    <input
                      id="customer-name"
                      type="text"
                      name="customer_name"
                      value={formData.customer_name}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      required
                      aria-required="true"
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${touched.customer_name && errors.customer_name
                        ? 'border-red-500 focus:ring-red-500'
                        : 'border-gray-300 focus:ring-blue-500'
                        }`}
                    />
                    {touched.customer_name && errors.customer_name && (
                      <p className="mt-1 text-sm text-red-600">{errors.customer_name}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="customer-phone" className="block text-sm font-medium text-gray-700 mb-2">
                      Customer Phone *
                    </label>
                    <input
                      id="customer-phone"
                      type="tel"
                      name="customer_phone"
                      value={formData.customer_phone}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      required
                      aria-required="true"
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${touched.customer_phone && errors.customer_phone
                        ? 'border-red-500 focus:ring-red-500'
                        : 'border-gray-300 focus:ring-blue-500'
                        }`}
                    />
                    {touched.customer_phone && errors.customer_phone && (
                      <p className="mt-1 text-sm text-red-600">{errors.customer_phone}</p>
                    )}
                  </div>

                  {/* Email is now optional and collapsible for speed */}
                  <details className="group">
                    <summary className="text-xs text-gray-500 cursor-pointer hover:text-blue-600 mb-2 list-none flex items-center">
                      <span className="mr-1">▶</span> Add Email (Optional)
                    </summary>
                    <div className="pl-4 border-l-2 border-gray-100">
                      <label htmlFor="customer-email" className="block text-sm font-medium text-gray-700 mb-1">
                        Customer Email
                      </label>
                      <input
                        id="customer-email"
                        type="email"
                        name="customer_email"
                        value={formData.customer_email}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </details>
                </>
              )}
            </div>

            {/* Services Selection */}
            <div className="space-y-4">
              <label className="block text-sm font-medium text-gray-700 flex flex-wrap items-center gap-2">
                <span>Select Services</span>
                <span className="text-gray-400 cursor-help relative group">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div className="hidden sm:block absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-48 bg-gray-800 text-white text-xs rounded p-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                    You can select multiple services for a single appointment.
                  </div>
                </span>
              </label>

              <div className="flex flex-col sm:flex-row gap-3">
                <div className="flex-1">
                  <select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="w-full border border-gray-300 rounded-xl shadow-sm py-2 px-4 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-gray-50 transition-all font-medium"
                  >
                    <option value="All">All Categories</option>
                    {categories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
                <div className="flex-[2] relative">
                  <input
                    type="text"
                    placeholder="Search services..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full border border-gray-300 rounded-xl shadow-sm py-2 px-4 pr-10 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-gray-50 transition-all"
                  />
                  <div className="absolute right-3 top-2.5 text-gray-400">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-3 max-h-[350px] overflow-y-auto border border-gray-100 rounded-2xl p-2 bg-gray-50/50">
                {servicesLoading ? (
                  <div className="col-span-full text-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-4"></div>
                    <p className="text-gray-500 text-sm">Loading services...</p>
                  </div>
                ) : filteredServices.length === 0 ? (
                  <div className="col-span-full text-center py-12 text-gray-500">
                    <span className="text-3xl block mb-2">🔍</span>
                    No services found
                  </div>
                ) : (
                  filteredServices.map(service => (
                    <div
                      key={service.id}
                      onClick={() => handleServiceSelection(service.id)}
                      className={`cursor-pointer p-4 border rounded-xl transition-all shadow-sm group hover:shadow-md ${formData.service_ids.includes(service.id)
                        ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500'
                        : 'border-white bg-white hover:border-blue-200'
                        }`}
                    >
                      <div className="flex justify-between items-center">
                        <div className="flex-1">
                          <h4 className={`font-bold text-sm ${formData.service_ids.includes(service.id) ? 'text-blue-900' : 'text-gray-900'}`}>{service.name}</h4>
                          <div className="mt-1 text-[10px] text-gray-500 flex items-center gap-2">
                            <span className="flex items-center gap-1 bg-gray-100 px-1.5 py-0.5 rounded">
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              {service.duration}{service.max_duration ? `-${service.max_duration}` : ''} mins
                            </span>
                            <span className="bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded uppercase font-bold tracking-tighter">
                              {service.category || 'Service'}
                            </span>
                          </div>
                        </div>
                        <div className="text-right">
                          <span className={`text-sm font-black ${formData.service_ids.includes(service.id) ? 'text-blue-600' : 'text-gray-900'}`}>
                            ₦{parseFloat(service.price).toLocaleString()}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
              {errors.service_ids && <p className="text-red-500 text-xs mt-1">{errors.service_ids}</p>}
            </div>
            {/* Worker Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Workers *
              </label>
              {workersLoading ? (
                <div className="text-center py-4 text-gray-500">
                  <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500 mr-2"></div>
                  Loading workers...
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-2 max-h-64 overflow-y-auto border border-gray-100 rounded-2xl p-2 bg-gray-50/50">
                  {Array.isArray(workers) && workers.map(worker => {
                    const isSelected = Array.isArray(formData.workers) && formData.workers.some(w => w.worker_id === worker.id);
                    const statusColor = {
                      'available': 'bg-green-100 text-green-700',
                      'busy': 'bg-red-100 text-red-700',
                      'offline': 'bg-gray-200 text-gray-700'
                    }[worker.current_status] || 'bg-gray-100 text-gray-800';

                    return (
                      <div
                        key={worker.id}
                        onClick={() => handleWorkerSelection(worker.id)}
                        className={`p-3 rounded-xl border-2 cursor-pointer transition-all ${isSelected
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-white bg-white hover:border-blue-100 hover:bg-white'
                          }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg ${isSelected ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-400'}`}>
                            {worker.name.charAt(0)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className={`font-bold text-sm truncate ${isSelected ? 'text-blue-900' : 'text-gray-900'}`}>{worker.name}</div>
                            <div className="text-xs text-gray-500">{worker.role}</div>
                          </div>
                          <div className="flex flex-col items-end gap-1">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-tighter ${statusColor}`}>
                              {worker.current_status || 'available'}
                            </span>
                            {isSelected && (
                              <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                              </svg>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              {touched.workers && errors.workers && (
                <p id="worker-error" className="mt-1 text-sm text-red-600" role="alert">
                  {errors.workers}
                </p>
              )}
              {!workersLoading && Array.isArray(workers) && workers.length === 0 && (
                <p className="mt-1 text-sm text-gray-500">No workers available</p>
              )}
            </div>
          </div>

          {/* Date and Time Selection */}
          {
            !isWalkInMode && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Date *
                  </label>
                  <Calendar
                    onChange={handleDateChange}
                    value={formData.booking_date}
                    className="w-full border rounded-md p-2 text-sm"
                    tileClassName={({ date, view }) => {
                      if (view === 'month') {
                        // Check if date has available slots (simplified check)
                        return 'hover:bg-blue-50 cursor-pointer rounded-full';
                      }
                    }}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Time *
                  </label>
                  {availableSlots.length > 0 ? (
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-60 overflow-y-auto p-1">
                      {availableSlots.map((slot, index) => (
                        <button
                          key={index}
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, booking_time: slot.time }))}
                          className={`py-2 px-3 text-sm rounded-md border ${formData.booking_time === slot.time
                            ? 'bg-blue-500 text-white border-blue-500'
                            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                            }`}
                        >
                          {slot.label}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="text-gray-500 italic p-4 border border-gray-200 rounded-md bg-gray-50 text-center">
                      {formData.booking_date ? 'No slots available for this date' : 'Select a date first'}
                    </div>
                  )}
                </div>
              </div>
            )
          }
          {/* Miscellaneous Charges */}
          <div className="border-t border-gray-100 pt-4 mt-4">
            <div className="flex justify-between items-center mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Miscellaneous Charges
              </label>
              <button
                type="button"
                onClick={() => setFormData(prev => ({
                  ...prev,
                  misc_charges: [...prev.misc_charges, { name: '', amount: '' }]
                }))}
                className="text-sm text-blue-600 hover:text-blue-800 font-medium"
              >
                + Add Charge
              </button>
            </div>
            {formData.misc_charges.map((charge, index) => (
              <div key={index} className="flex gap-2 mb-2 items-center">
                <input
                  type="text"
                  placeholder="Charge Description (e.g., Extra Glue)"
                  value={charge.name}
                  onChange={(e) => {
                    const newCharges = [...formData.misc_charges];
                    newCharges[index].name = e.target.value;
                    setFormData(prev => ({ ...prev, misc_charges: newCharges }));
                  }}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
                />
                <input
                  type="number"
                  placeholder="Amount (₦)"
                  value={charge.amount}
                  onChange={(e) => {
                    const newCharges = [...formData.misc_charges];
                    newCharges[index].amount = e.target.value;
                    setFormData(prev => ({ ...prev, misc_charges: newCharges }));
                  }}
                  className="w-32 px-3 py-2 border border-gray-300 rounded-md text-sm"
                  min="0"
                  step="100"
                />
                <button
                  type="button"
                  onClick={() => {
                    const newCharges = formData.misc_charges.filter((_, i) => i !== index);
                    setFormData(prev => ({ ...prev, misc_charges: newCharges }));
                  }}
                  className="text-red-500 hover:text-red-700 font-bold px-2"
                >
                  &times;
                </button>
              </div>
            ))}
            {formData.misc_charges.length === 0 && (
              <p className="text-sm text-gray-500 italic">No miscellaneous charges added.</p>
            )}
          </div>

          {/* Notes */}
          <div>
            <label htmlFor="booking-notes" className="block text-sm font-medium text-gray-700 mb-2">
              Additional Notes
            </label>
            <textarea
              id="booking-notes"
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Any special requests or notes..."
              aria-describedby="notes-description"
            />
            <span id="notes-description" className="sr-only">Optional field for any special requests or additional information</span>
          </div>
          {/* Form Buttons */}
          <div className="flex flex-col-reverse sm:flex-row gap-3 sm:space-x-4 pt-4 border-t border-gray-100">
            <button
              type="button"
              onClick={onCancel}
              className="w-full sm:flex-1 bg-white text-gray-700 py-2.5 px-4 border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 font-medium transition-colors"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || (!isWalkInMode && !formData.booking_time)}
              className="w-full sm:flex-1 bg-blue-600 text-white py-2.5 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed font-medium shadow-sm transition-colors"
              aria-describedby="submit-status"
            >
              {loading ? (isEditing ? 'Updating...' : 'Adding...') :
                (isEditing ? 'Update Booking' : 'Add Walk-in Customer')}
            </button>
          </div>
          <div id="submit-status" className="sr-only" role="status" aria-live="polite">
            {loading ? (isEditing ? 'Updating booking' : 'Processing your booking request') : 'Ready to save booking'}
          </div>
        </form>
      </div>
    </div>
  );
};

export default BookingForm;